import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
// prettier-ignore
import {
  FF_ACCOUNT_INFO_V2,
  FF_CO_ADDRESS_EDIT_STANDARD_USER,
  FF_STATIC_BLOCKED_VAT_IDS
} from '~app/modules/authorization/constants/authorization.constant';
import { BLOCKED_IT_VAT_IDS } from '~app/modules/checkout/constants/blocked-vat-ids.constants';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { isSoftLoggedIn } from '~server/utils/auth.utils';
import { ApiConfig, APP_EMPLOYEE_CUSTOMERS_LIST, BLOCKED_ACCOUNT_PAGE } from '~shared/constants';

import { AccountStatusEnum, USER_ROLES, USER_STATUS_MAP } from '~shared/constants/account-info.constant';
import {
  AccountInfoFetchingStatus,
  AccountType,
  AccountTypeInfo,
  IAccountInformation,
  IAccountInformationV2,
  IAccountPersonalInformation,
  IEmployee,
  ORGANIZATION_ROLE_ADMIN,
} from '~shared/models';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import {
  ACCOUNT_INFO_V1_REQUEST_URL,
  ACCOUNT_INFO_V2_REQUEST_URL,
  ACCOUNT_TYPE_KEY,
  HAS_DIGITAL_IDENTITY_KEY,
  IMPERSONATED_CUSTOMER_FULLNAME_KEY,
  SUB_ACCOUNT_TYPE_KEY,
  USER_TYPE_KEY,
} from '../constants/identity.constant';
import * as Sentry from '@sentry/browser';

@Injectable({
  providedIn: 'root',
})
export class AccountInfoService {
  private readonly accountInfo$: Observable<IAccountInformation | null>;
  private readonly accountInfoV2$: Observable<IAccountInformationV2 | null>;
  private readonly accountInfoPersonal$: Observable<IAccountPersonalInformation | null>;
  private accountInfoSubjectPersonal$ = new ReplaySubject<IAccountPersonalInformation | null>(1);
  private accountInfoSubject$ = new ReplaySubject<IAccountInformation | null>(1);
  private accountInfoSubjectV2$ = new ReplaySubject<IAccountInformationV2 | null>(1);
  FF_ENABLE_ITALY_SPLIT_PAYMENT = 'FF_ENABLE_ITALY_SPLIT_PAYMENT';
  private employeeAccountInfo$ = new ReplaySubject<IEmployee | null>(1);
  public readonly accountType$ = new ReplaySubject<AccountType | null>(1);
  public readonly accountInfoFetchingStatus$ = new ReplaySubject<AccountType | AccountInfoFetchingStatus>(1);
  isAccountInfoV2APIEnabled$ =
    this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2);
  public static impersonationSessionId: string | null = null;
  public static readonly HTTPImpersonateConflictCode = 409;

  constructor(
    private httpClient: HttpClient,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private serverLogger: ServerLoggerService
  ) {
    this.accountInfo$ = this.accountInfoSubject$.asObservable();
    this.accountInfoV2$ = this.accountInfoSubjectV2$.asObservable();
    this.accountInfoSubject$.next(null);

    this.accountInfoPersonal$ = this.accountInfoSubjectPersonal$.asObservable();
    this.accountInfoSubjectPersonal$.next(null);

    this.isAccountBlocked()
      .pipe(
        filter((blocked) => blocked),
        take(1)
      )
      .subscribe(() => {
        this.router.navigateByUrl(BLOCKED_ACCOUNT_PAGE);
      });

    this.employeeAccountInfo$.next(null);
    this.accountType$.next(null);
  }

  public isSplitPaymentObliged(): Observable<boolean> {
    return this.activeFeaturesConfigcatService
      .isFeatureEnabledReadyForCurrentCountry$(this.FF_ENABLE_ITALY_SPLIT_PAYMENT)
      .pipe(
        switchMap((enabled) =>
          enabled ? this.accountInfo$.pipe(filter((info: IAccountInformation) => info !== null)) : of(null)
        ),
        map((info: IAccountInformation) => {
          return info ? info.hasSplitPaymentObligation : false;
        })
      );
  }

  public isAccountValidated(): Observable<boolean> {
    return this.getAccountStatus().pipe(map((status) => status === AccountStatusEnum.validated));
  }

  public isAccountBlocked(): Observable<boolean> {
    return this.getAccountStatus().pipe(map((status) => status === AccountStatusEnum.blocked));
  }

  public getAccountInfo(): Observable<IAccountInformation | null> {
    return this.accountInfo$;
  }

  public getAccountInfoV2(): Observable<IAccountInformationV2 | null> {
    return this.accountInfoV2$;
  }

  public getEmployeeAccountInfo(): Observable<IEmployee | null> {
    return this.employeeAccountInfo$;
  }

  public isBlockedVatIdAccount(): Observable<boolean> {
    return this.getAccountInfo().pipe(
      mergeMap((accountInfo) =>
        this.activeFeaturesConfigcatService
          .isFeatureEnabledAndReadyV2(FF_STATIC_BLOCKED_VAT_IDS)
          .pipe(map((enabled) => ({ enabled, accountInfo })))
      ),
      map(({ enabled, accountInfo }) => enabled && BLOCKED_IT_VAT_IDS.includes(accountInfo?.organizationVatNumber))
    );
  }

  public getPerosnalAccountInfo(): Observable<IAccountPersonalInformation | null> {
    return this.accountInfoPersonal$;
  }

  public isAccountEligibleAddressActions(): Observable<boolean> {
    return combineLatest([
      this.accountInfo$,
      this.activeFeaturesConfigcatService.isFeatureEnabled(FF_CO_ADDRESS_EDIT_STANDARD_USER),
      this.isImpersonationSessionActive(),
    ]).pipe(
      map(([accountInformation, isFFAddressEditEnabled, isImpersonating]) => {
        if (!accountInformation || accountInformation.activeContext === 'personal') {
          return true;
        }

        if (isImpersonating) {
          return false;
        }

        if (isFFAddressEditEnabled) {
          return true;
        }

        return (
          accountInformation.organizationRoles && accountInformation.organizationRoles.includes(ORGANIZATION_ROLE_ADMIN)
        );
      }),
      distinctUntilChanged()
    );
  }

  getAccountRole(): Observable<string> {
    return this.accountInfo$.pipe(
      map((accountInformation) => accountInformation && accountInformation.role),
      distinctUntilChanged()
    );
  }

  getActiveContext(): Observable<string> {
    return this.accountInfo$.pipe(
      filter((accountInformation) => accountInformation && !!accountInformation.activeContext),
      map((accountInformation) => accountInformation.activeContext),
      distinctUntilChanged()
    );
  }

  public isEntrepreneur(): Observable<boolean> {
    return this.accountInfo$.pipe(
      map((accountInformation) => accountInformation && accountInformation.role === USER_ROLES.entrepreneur),
      distinctUntilChanged()
    );
  }

  public isConsumer(): Observable<boolean> {
    return this.accountInfo$.pipe(
      map((accountInformation) => accountInformation && accountInformation.role === USER_ROLES.consumer),
      distinctUntilChanged()
    );
  }

  public isSubsidiaryAccount(): Observable<boolean> {
    return this.accountInfo$.pipe(
      map((accountInformation) => accountInformation && accountInformation.accountType === AccountTypeInfo.SUBSIDIARY),
      distinctUntilChanged()
    );
  }

  public getAccountStatus(): Observable<AccountStatusEnum | null> {
    return this.accountInfo$.pipe(map((accountInformation) => accountInformation && accountInformation.status));
  }

  public isFirstTimeLogin(): Observable<boolean> {
    return this.accountInfo$.pipe(
      map((accountInformation) => accountInformation?.isFirstLogin === true),
      distinctUntilChanged()
    );
  }

  public isImpersonationSessionActive(): Observable<boolean> {
    return this.isAccountInfoV2APIEnabled$.pipe(
      mergeMap((enabled) => {
        if (enabled) {
          return this.accountInfoFetchingStatus$.pipe(
            filter((status) => {
              return (
                [AccountType.CUSTOMER, AccountType.EMPLOYEE, AccountType.EMPLOYEE_AS_CUSTOMER].includes(
                  status as AccountType
                ) || isSoftLoggedIn()
              );
            }),
            map((accountType) => accountType === AccountType.EMPLOYEE_AS_CUSTOMER)
          );
        } else {
          return of(false);
        }
      })
    );
  }

  public removeAccountInfo(): void {
    this.accountInfoSubject$.next(null);
  }

  public removeEmployeeAccountInfo(): void {
    this.employeeAccountInfo$.next(null);
    this.accountType$.next(null);

    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem(ACCOUNT_TYPE_KEY);
    CheckoutCommonUtil.clearSubsidiaryNeedsApprovalFromLocalStorage();
    localStorage.removeItem(IMPERSONATED_CUSTOMER_FULLNAME_KEY);
  }

  public fetchAccountInfo(): Observable<IAccountInformation | IAccountInformationV2> {
    this.accountInfoFetchingStatus$.next(AccountInfoFetchingStatus.LOADING);
    return this.isAccountInfoV2APIEnabled$.pipe(
      switchMap((isEnabled) => {
        const accountInfoURL = isEnabled ? ACCOUNT_INFO_V2_REQUEST_URL() : ACCOUNT_INFO_V1_REQUEST_URL();
        return this.httpClient
          .get<IAccountInformation | IAccountInformationV2>(`${accountInfoURL}?d=${new Date().getTime().toString()}`)
          .pipe(
            tap(this.setupAccountInfo(isEnabled)),
            catchError((error: any) => {
              if (isPlatformBrowser(this.platformId)) {
                this.serverLogger.error('BuyerAccount account-info failed', {
                  ...error,
                  correlationId: error?.sourceError?.headers?.get('x-correlation-id'),
                });
              }
              this.accountInfoFetchingStatus$.next(AccountInfoFetchingStatus.FAILED);
              return throwError(error);
            })
          );
      })
    );
  }

  public impersonateCustomer(customerCdmId: string, businessCdmId?: string, customerEmail: string = null) {
    const body = {
      customerCdmId,
      businessCdmId,
      customerEmail,
    };

    return this.httpClient
      .put(`${ApiConfig.SVC_BUYER_ACCOUNT_EMPLOYEE_IMPERSONATE_API_URL()}`, body)
      .pipe(mergeMap((res) => this.fetchAccountInfo().pipe(map(() => res))));
  }

  private setAccountInfo(accountInfo: IAccountInformation): void {
    localStorage.setItem(SUB_ACCOUNT_TYPE_KEY, accountInfo.accountType ?? '');
    this.accountInfoSubject$.next({
      ...accountInfo,
      status: USER_STATUS_MAP[accountInfo.status],
    });
  }

  public setPersonalAccountInfo(activeContext: string): void {
    this.accountInfoSubjectPersonal$.next({ activeContext });
  }

  private setEmployeeAccountInfo(employeeInfo: IEmployee): void {
    this.employeeAccountInfo$.next(employeeInfo);
  }

  private setupAccountInfo(isEnabled: boolean): (accountInfo: IAccountInformation | IAccountInformationV2) => void {
    return (accountInfo) => {
      const id = (accountInfo as IAccountInformationV2)?.customer?.hashedEmail;
      if (id) {
        // eslint-disable-next-line no-console
        console.log(`(customer type) ${(accountInfo as IAccountInformationV2)?.type}`);
        Sentry.getCurrentScope().setUser({ id, email: id });
      }
      this.accountInfoSubjectV2$.next(accountInfo as IAccountInformationV2);
      if (!isEnabled) {
        this.setAccountInfo(accountInfo as IAccountInformation);
        this.accountInfoFetchingStatus$.next(AccountType.CUSTOMER);
        localStorage.setItem(USER_TYPE_KEY, 'CUST');
        return;
      }
      const accountInfoV2 = accountInfo as IAccountInformationV2;

      localStorage.setItem(ACCOUNT_TYPE_KEY, accountInfoV2.type);
      this.accountInfoFetchingStatus$.next(accountInfoV2.type);

      localStorage.setItem(
        USER_TYPE_KEY,
        [AccountType.EMPLOYEE, AccountType.EMPLOYEE_AS_CUSTOMER].includes(accountInfoV2.type) ? 'EMP' : 'CUST'
      );

      this.accountType$.next(accountInfoV2.type);
      if (accountInfo[AccountType.CUSTOMER]) {
        this.setAccountInfo(accountInfoV2.customer);
      }
      if (accountInfo[AccountType.EMPLOYEE]) {
        this.setEmployeeAccountInfo(accountInfoV2.employee);
        AccountInfoService.impersonationSessionId = accountInfoV2.employee?.impersonationSessionId;
      }
      if (accountInfoV2.type === AccountType.EMPLOYEE_AS_CUSTOMER) {
        localStorage.setItem(HAS_DIGITAL_IDENTITY_KEY, 'true');
        localStorage.setItem(
          IMPERSONATED_CUSTOMER_FULLNAME_KEY,
          `${accountInfoV2.customer?.firstName ?? ''} ${accountInfoV2.customer?.lastName ?? ''}`
        );
      }
      if (accountInfoV2.type === AccountType.EMPLOYEE) {
        this.router.navigate([APP_EMPLOYEE_CUSTOMERS_LIST]).then(() => {
          this.removeAccountInfo();
        });
      }
    };
  }
  public getFeatureFlagEnabledForUser(featureFlagName: string): Observable<boolean> {
    return this.accountInfo$.pipe(
      take(1),
      switchMap((accountInformation) => {
        return this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(
          featureFlagName,
          null,
          accountInformation?.email
        );
      })
    );
  }
}
